import { memo, useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import styled from 'styled-components'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TopDestinations from 'components/Common/TopDestinationsList/TopDestinations'
import Heading from 'LuxKit/Typography/Heading'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import TabButton from 'LuxKit/Tabs/TabButton'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import config from 'constants/config'
import { getConfigGeographicPlaceId } from 'lib/places/configGeographicPlaceIds'
import { getTopDestinationLink } from 'lib/places/getTopDestinationLink'
import { useLocalizedPlaces } from 'hooks/usePlace'
import { rem } from 'polished'

interface DestinationBase {
  name: string
  placeId?: string
  level: string
  link?: string
  alternativeNameForLink?: string
}

export interface CountryDestination extends DestinationBase {}

export interface CityDestination extends DestinationBase {
  higherLevel: string
  country: string
}

export type TopDestinationDisplayItem = (CountryDestination | CityDestination) & {
  displayName?: string
}

const addLinksToDestinations = (destinationArray: Array<CountryDestination | CityDestination>) => {
  return destinationArray.forEach((destination) => {
    destination.link = getTopDestinationLink(destination)
  })
}

function enrichDestinationList<T extends { name: string }>(items: Array<T>): Array<T & { placeId?: string }> {
  return items.map(item => ({
    ...item,
    placeId: getConfigGeographicPlaceId(item.name),
  }))
}

const enrichedTopDestinationList = {
  countries: enrichDestinationList(config.topDestinationList.countries),
  cities: enrichDestinationList(config.topDestinationList.cities),
  popular: enrichDestinationList(config.topDestinationList.popular),
}

const Root = styled.div`
  display: flex;
  text-align: start;

  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const StyledLayoutContainer = styled(LayoutContainer)`
  padding-top: ${rem(16)};
`

export type DestinationNavItemType = 'Countries' | 'Cities' | 'Popular'

const initDestinationLinks = () => {
  addLinksToDestinations(enrichedTopDestinationList.countries)
  addLinksToDestinations(enrichedTopDestinationList.cities)
  addLinksToDestinations(enrichedTopDestinationList.popular)
}
initDestinationLinks()

interface Props {
  destinationNavItems?: Array<DestinationNavItemType>
}

const defaultItems: Array<DestinationNavItemType> = ['Countries', 'Cities', 'Popular']
function TopDestinationList({ destinationNavItems = defaultItems }: Props) {
  const intl = useIntl()
  const [activeNavItem, setActiveNavItem] = useState<DestinationNavItemType>(destinationNavItems[0])

  const destinations = useMemo(() => {
    if (activeNavItem === 'Countries') {
      return enrichedTopDestinationList.countries
    }
    if (activeNavItem === 'Cities') {
      return enrichedTopDestinationList.cities
    }
    return enrichedTopDestinationList.popular
  }, [activeNavItem])

  const placeIds = useMemo(
    () => destinations.map(destination => destination.placeId).filter((id): id is string => !!id),
    [destinations],
  )
  const localizedPlaces = useLocalizedPlaces(placeIds)

  const destinationsWithDisplay = useMemo<Array<TopDestinationDisplayItem>>(() => {
    return destinations.map((destination) => {
      if (!destination.placeId) {
        return {
          ...destination,
          displayName: destination.name,
        }
      }
      const localized = localizedPlaces[destination.placeId]
      if (localized?.isUnavailable) {
        return {
          ...destination,
          displayName: destination.name,
        }
      }
      return {
        ...destination,
        displayName: localized?.place?.name,
      }
    })
  }, [destinations, localizedPlaces])

  const headingText = intl.formatMessage({
    id: 'zjJKc3_H85J6NZjMrxgN',
    defaultMessage: 'Explore top hotel destinations we love',
    description: 'Heading for top destinations list section on the hotel page',
  })

  const getNavItemLabel = useCallback(
    (nav: DestinationNavItemType) => {
      switch (nav) {
        case 'Countries':
          return intl.formatMessage({
            id: 'N7V9QT85zY81Mbz-gBbA',
            defaultMessage: 'Countries',
            description: 'Tab label for countries destinations in the top destinations list section',
          })
        case 'Cities':
          return intl.formatMessage({
            id: 'BSQEP-Z1EAoitBk8nxuF',
            defaultMessage: 'Cities',
            description: 'Tab label for cities destinations in the top destinations list section',
          })
        case 'Popular':
          return intl.formatMessage({
            id: 'Yb5wu_5J55tWEiRZ27AN',
            defaultMessage: 'Popular',
            description: 'Tab label for popular destinations in the top destinations list section',
          })
      }
    },
    [intl],
  )

  return (<Root>
    <StyledLayoutContainer>
      <VerticalSpacer gap={16}>
        <Heading variant="heading3">{headingText}</Heading>
        <TabButtonsGroup>
          {destinationNavItems.map((nav) => {
            return <TabButton
              key={nav}
              size="medium"
              isActive={nav === activeNavItem}
              onClick={() => { setActiveNavItem(nav) }}
            >
              {getNavItemLabel(nav)}
            </TabButton>
          })}
        </TabButtonsGroup>
        <CSSBreakpoint max="tablet">
          <TopDestinations
            destinations={destinationsWithDisplay}
            toShowByDefault={6}
            destinationType={activeNavItem}
          />
        </CSSBreakpoint>
        <CSSBreakpoint min="desktop">
          <TopDestinations
            destinations={destinationsWithDisplay}
            toShowByDefault={12}
            destinationType={activeNavItem}
          />
        </CSSBreakpoint>
      </VerticalSpacer>
    </StyledLayoutContainer>
  </Root>)
}

export default memo(TopDestinationList)
